import React, { useState } from 'react';
import {
  X,
  FileText,
  ExternalLink,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  RotateCw,
  Shield,
  AlertCircle,
} from 'lucide-react';
import { AdminDocumentItem } from './DocumentsCenter';
import { safeFetch } from '../../lib/api';
import {
  tryClaimEndpointDocumentDecision,
  releaseEndpointDocumentDecision,
} from '../../lib/documentReviewGuard';
import {
  adminSafeError,
  describeDocumentEngine,
  describeDocumentVerdict,
  hasIdentityMismatch,
  isDocumentUndecided,
  type AdminSafeError,
} from '../../lib/documentsCentreModel';

interface DocumentPreviewModalProps {
  document: AdminDocumentItem | null;
  onClose: () => void;
  onOpenApplication: (appId: string) => void;
  onRefresh: () => void;
}

const VERDICT_TONE_CLASSES: Record<string, string> = {
  emerald: 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200',
  red: 'bg-red-50 text-red-700 ring-1 ring-red-200',
  amber: 'bg-amber-50 text-amber-700 ring-1 ring-amber-200',
  slate: 'bg-slate-100 text-slate-600 ring-1 ring-slate-200',
};

/** Extracted-field values arrive as scalars or { value, confidence }. */
function formatExtractedFieldValue(raw: any): string {
  if (raw === null || raw === undefined) return '—';
  if (typeof raw === 'object') {
    const value = raw.value ?? '';
    const confidence = typeof raw.confidence === 'number' ? ` (${Math.round(raw.confidence * 100)}% confidence)` : '';
    return `${value}${confidence}` || '—';
  }
  return String(raw);
}

export const DocumentPreviewModal: React.FC<DocumentPreviewModalProps> = ({
  document: doc,
  onClose,
  onOpenApplication,
  onRefresh,
}) => {
  if (!doc) return null;

  const [activeTab, setActiveTab] = useState<'preview' | 'details' | 'history' | 'override'>('preview');
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [actionError, setActionError] = useState<AdminSafeError | null>(null);
  const [actionSuccess, setActionSuccess] = useState<string | null>(null);

  // Human decision inputs
  const [rejectionReason, setRejectionReason] = useState<string>('');

  // Override inputs
  const [overrideTargetStatus, setOverrideTargetStatus] = useState<string>('Approved');
  const [overrideReason, setOverrideReason] = useState<string>('');

  const engine = describeDocumentEngine(doc.extraction);
  const verdictChip = describeDocumentVerdict(doc.verdict);
  const mismatch = hasIdentityMismatch({ verdict: doc.verdict, judgment: doc.judgment, aiReview: doc.aiReview });
  const undecided = isDocumentUndecided(doc);
  const escalated = doc.systemDecision === 'Further Human Review Required';

  /**
   * Double-fire protection (#1231 / I5): every decision write claims
   * the document's flight SYNCHRONOUSLY at click time via the shared
   * documentReviewGuard registry — before any await — so a double
   * click can never start a second write, whichever centre surface
   * mounted the button. The write itself stays on the hardened
   * centre endpoints (the server composes it, PR #1236).
   */
  const claimDecisionFlight = (): boolean => {
    const flightKey = `${doc.applicationId}:${doc.docId}`;
    if (!tryClaimEndpointDocumentDecision(flightKey)) return false;
    setSubmitting(true);
    setActionError(null);
    setActionSuccess(null);
    return true;
  };
  const releaseDecisionFlight = () => {
    releaseEndpointDocumentDecision(`${doc.applicationId}:${doc.docId}`);
    setSubmitting(false);
  };

  const handleHumanDecision = async (decision: 'Approved' | 'Rejected') => {
    if (decision === 'Rejected' && !rejectionReason.trim()) {
      setActionError({ safe: 'A rejection reason is required when rejecting a document.', raw: '' });
      return;
    }
    if (!claimDecisionFlight()) return;

    try {
      const res = await safeFetch(`/api/admin/documents/${doc.docId}/human-decision`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          applicationId: doc.applicationId,
          decision,
          rejectionReason,
        }),
      });

      if (res && res.success) {
        setActionSuccess(`Document decision successfully recorded as ${decision}.`);
        onRefresh();
      } else {
        setActionError(adminSafeError({ message: res?.error || 'Failed to record decision.' }, 'Failed to record the decision. Please try again.'));
      }
    } catch (err: any) {
      console.error('Failed to record human decision:', err);
      setActionError(adminSafeError(err, 'Failed to record the decision. Please try again.'));
    } finally {
      releaseDecisionFlight();
    }
  };

  const handleAdminOverride = async () => {
    if (!overrideReason.trim() || overrideReason.trim().length < 20) {
      setActionError({ safe: 'An explicit override reason of at least 20 characters is required.', raw: '' });
      return;
    }
    if (!claimDecisionFlight()) return;

    try {
      const res = await safeFetch(`/api/admin/documents/${doc.docId}/override`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          applicationId: doc.applicationId,
          targetStatus: overrideTargetStatus,
          overrideReason: overrideReason.trim(),
        }),
      });

      if (res && res.success) {
        setActionSuccess(`Administrative override applied: status changed to ${overrideTargetStatus}.`);
        onRefresh();
      } else {
        setActionError(adminSafeError({ message: res?.error || 'Failed to apply override.' }, 'Failed to apply the override. Please try again.'));
      }
    } catch (err: any) {
      console.error('Failed to apply override:', err);
      setActionError(adminSafeError(err, 'Failed to apply the override. Please try again.'));
    } finally {
      releaseDecisionFlight();
    }
  };

  const handleRetryJob = async () => {
    if (!claimDecisionFlight()) return;

    try {
      const res = await safeFetch(`/api/admin/documents/${doc.docId}/retry`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ applicationId: doc.applicationId }),
      });

      if (res && res.success) {
        setActionSuccess('Document review processing job successfully re-queued.');
        onRefresh();
      } else {
        setActionError(adminSafeError({ message: res?.error || 'Failed to retry processing job.' }, 'Failed to retry the review. Please try again.'));
      }
    } catch (err: any) {
      console.error('Failed to retry document review:', err);
      setActionError(adminSafeError(err, 'Failed to retry the review. Please try again.'));
    } finally {
      releaseDecisionFlight();
    }
  };

  // Structured findings sources (rendered only when actually present).
  const judgment = (doc.judgment || {}) as Record<string, any>;
  const checks: any[] = Array.isArray(judgment.checks)
    ? judgment.checks
    : Array.isArray((doc.aiReview as any)?.checks)
      ? (doc.aiReview as any).checks
      : [];
  const extractedFields: Record<string, any> | null =
    ((doc as any).extraction?.extractedFields as Record<string, any>) ||
    (judgment.extractedFields as Record<string, any>) ||
    null;
  const identityStatus = doc.verdict?.identityStatus ?? judgment.identityStatus ?? null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-fade-in overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-5xl my-8 overflow-hidden flex flex-col max-h-[90vh]">
        {/* Modal Header */}
        <div className="px-6 py-4 border-b border-slate-200 bg-slate-50 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-brand/10 text-brand rounded-xl">
              <FileText className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-base font-bold text-slate-900 flex items-center gap-2">
                {doc.documentName}
                <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-slate-200 text-slate-700">
                  {doc.version}
                </span>
              </h2>
              <p className="text-xs text-slate-500">
                {doc.documentType} • Applicant: <span className="font-semibold text-slate-800">{doc.applicantName}</span> ({doc.applicantEmail})
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => {
                onClose();
                onOpenApplication(doc.applicationId);
              }}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-brand bg-brand/10 hover:bg-brand/20 rounded-lg transition-colors cursor-pointer"
            >
              View Application
              <ExternalLink className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={onClose}
              className="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-200/60 transition-colors cursor-pointer"
              aria-label="Close modal"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Action Status Banners — admin-safe copy; raw text only in details */}
        {actionError && (
          <div className="bg-red-50 border-b border-red-200 p-3 text-xs text-red-800 font-medium">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-4 h-4 shrink-0 text-red-600" />
              {actionError.safe}
            </div>
            {actionError.raw && (
              <details className="mt-1.5 ml-6">
                <summary className="text-[11px] text-red-400 cursor-pointer hover:text-red-600">Technical details</summary>
                <p className="text-[11px] font-mono text-red-600 break-words mt-0.5">{actionError.raw}</p>
              </details>
            )}
          </div>
        )}
        {actionSuccess && (
          <div className="bg-emerald-50 border-b border-emerald-200 p-3 text-xs text-emerald-800 font-medium flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 shrink-0 text-emerald-600" />
            {actionSuccess}
          </div>
        )}

        {/* Content Tabs */}
        <div className="border-b border-slate-200 bg-white px-6 shrink-0">
          <nav className="flex space-x-6 text-xs font-semibold">
            <button
              onClick={() => setActiveTab('preview')}
              className={`py-3 border-b-2 transition-colors cursor-pointer ${
                activeTab === 'preview' ? 'border-brand text-brand' : 'border-transparent text-slate-500 hover:text-slate-800'
              }`}
            >
              Document File & Preview
            </button>
            <button
              onClick={() => setActiveTab('details')}
              className={`py-3 border-b-2 transition-colors cursor-pointer ${
                activeTab === 'details' ? 'border-brand text-brand' : 'border-transparent text-slate-500 hover:text-slate-800'
              }`}
            >
              Decision Rationale & Findings
            </button>
            <button
              onClick={() => setActiveTab('history')}
              className={`py-3 border-b-2 transition-colors cursor-pointer ${
                activeTab === 'history' ? 'border-brand text-brand' : 'border-transparent text-slate-500 hover:text-slate-800'
              }`}
            >
              Version History & Audit Trail
            </button>
            <button
              onClick={() => setActiveTab('override')}
              className={`py-3 border-b-2 transition-colors cursor-pointer ${
                activeTab === 'override' ? 'border-brand text-brand' : 'border-transparent text-slate-500 hover:text-slate-800'
              }`}
            >
              Admin Override
            </button>
          </nav>
        </div>

        {/* Modal Body */}
        <div className="p-6 overflow-y-auto flex-1 space-y-6">
          {activeTab === 'preview' && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Document File Viewer */}
              <div className="md:col-span-2 bg-slate-100 rounded-xl border border-slate-200 p-4 min-h-[350px] flex flex-col items-center justify-center">
                {doc.downloadUrl ? (
                  doc.mimeType?.includes('pdf') || doc.fileName.endsWith('.pdf') ? (
                    <iframe
                      src={doc.downloadUrl}
                      title="PDF Preview"
                      className="w-full h-[400px] rounded-lg border border-slate-300"
                    />
                  ) : (
                    <img
                      src={doc.downloadUrl}
                      alt={doc.documentName}
                      className="max-h-[400px] object-contain rounded-lg shadow-sm"
                    />
                  )
                ) : (
                  <div className="text-center p-6">
                    <FileText className="w-12 h-12 text-slate-400 mx-auto mb-2" />
                    <p className="text-xs font-semibold text-slate-700">Preview not directly available in frame.</p>
                    <p className="text-[11px] text-slate-500 mt-1">Download or inspect metadata using secure link.</p>
                  </div>
                )}
              </div>

              {/* Sidebar Document Overview */}
              <div className="space-y-4">
                <div className="bg-slate-50 rounded-xl p-4 border border-slate-200 space-y-3 text-xs">
                  <h3 className="font-bold text-slate-900 border-b border-slate-200 pb-2">Document Metadata</h3>
                  <div>
                    <span className="text-slate-500 block">Application ID:</span>
                    <span className="font-mono font-bold text-slate-800">{doc.applicationId}</span>
                  </div>
                  <div>
                    <span className="text-slate-500 block">Program:</span>
                    <span className="font-semibold text-slate-800">{doc.program}</span>
                  </div>
                  <div>
                    <span className="text-slate-500 block">Uploaded Date & Time:</span>
                    <span className="font-medium text-slate-800">
                      {new Date(doc.uploadedAt).toLocaleString()}
                    </span>
                  </div>
                  <div>
                    <span className="text-slate-500 block">Uploaded By:</span>
                    <span className="font-semibold text-slate-800">{doc.uploadedBy}</span>
                  </div>
                  <div>
                    <span className="text-slate-500 block">Persisted Status:</span>
                    <span className="font-bold text-slate-800">{doc.status}</span>
                  </div>
                  <div>
                    <span className="text-slate-500 block">System Decision:</span>
                    <span className="font-bold text-brand">{doc.systemDecision}</span>
                  </div>
                  <div>
                    <span className="text-slate-500 block">Scanned by:</span>
                    <span
                      className="inline-flex items-center px-1.5 py-px rounded-md text-[10px] font-semibold bg-slate-100 text-slate-500 ring-1 ring-slate-200"
                      title={engine.title}
                    >
                      {engine.label}
                    </span>
                    {doc.extraction?.processedAt && (
                      <span className="block text-slate-400 mt-1">
                        {new Date(doc.extraction.processedAt).toLocaleString()}
                      </span>
                    )}
                  </div>
                  {verdictChip && (
                    <div>
                      <span className="text-slate-500 block">AI Verdict:</span>
                      <span className={`inline-flex items-center px-1.5 py-px rounded-md text-[10px] font-semibold ${VERDICT_TONE_CLASSES[verdictChip.tone]}`}>
                        {verdictChip.label}
                      </span>
                      {mismatch && (
                        <span className="block mt-1 font-bold text-red-600">Identity mismatch — review individually</span>
                      )}
                    </div>
                  )}
                </div>

                {/* Human Review Actions — available for EVERY undecided
                    document (D6), not only escalated ones; the server
                    already permits the decision (PR #1236 gates). */}
                {undecided && (
                  <div className={`rounded-xl p-4 border space-y-3 text-xs ${escalated ? 'bg-amber-50 border-amber-200' : 'bg-slate-50 border-slate-200'}`}>
                    <h3 className={`font-bold flex items-center gap-1.5 ${escalated ? 'text-amber-900' : 'text-slate-900'}`}>
                      <AlertTriangle className={`w-4 h-4 ${escalated ? 'text-amber-600' : 'text-slate-500'}`} />
                      {escalated ? 'Human Review Escalation' : 'Record a Human Decision'}
                    </h3>
                    <p className={escalated ? 'text-amber-800' : 'text-slate-600'}>
                      {escalated
                        ? 'The system could not finalize a decision automatically. Authorize a human approval or rejection below:'
                        : 'This document has not been finally decided. Record the workspace\u2019s decision below:'}
                    </p>
                    {verdictChip && (
                      <p className="text-slate-600">
                        System verdict: <span className="font-bold text-slate-800">{doc.verdict?.suggestedStatus}</span>
                        {identityStatus ? ` · Identity: ${identityStatus}` : ''}
                      </p>
                    )}
                    {doc.verdict?.reason && (
                      <p className="text-slate-700 bg-white/70 border border-slate-200 rounded-lg px-3 py-2">
                        <span className="font-bold text-slate-800">Why: </span>
                        {doc.verdict.reason}
                      </p>
                    )}
                    <textarea
                      placeholder="Optional notes or rejection reason..."
                      value={rejectionReason}
                      onChange={e => setRejectionReason(e.target.value)}
                      className={`w-full p-2 border rounded-lg text-xs bg-white focus:outline-none  focus:ring-[3px] focus:ring-brand-primary/15 ${escalated ? 'border-amber-300' : 'border-slate-300'}`}
                      rows={2}
                    />
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleHumanDecision('Approved')}
                        disabled={submitting}
                        className="flex-1 py-1.5 bg-success text-white font-bold rounded-lg hover:bg-[color-mix(in_srgb,var(--color-success)_90%,#000)] transition-colors cursor-pointer disabled:opacity-50"
                      >
                        Approve
                      </button>
                      <button
                        onClick={() => handleHumanDecision('Rejected')}
                        disabled={submitting}
                        className="flex-1 py-1.5 bg-red-600 text-white font-bold rounded-lg hover:bg-red-700 transition-colors cursor-pointer disabled:opacity-50"
                      >
                        Reject
                      </button>
                    </div>
                  </div>
                )}

                {/* Retry Action */}
                {doc.systemDecision === 'Review Failed' && (
                  <button
                    onClick={handleRetryJob}
                    disabled={submitting}
                    className="w-full py-2 bg-brand text-white font-bold rounded-xl hover:bg-brand/90 transition-colors shadow-sm flex items-center justify-center gap-2 text-xs cursor-pointer disabled:opacity-50"
                  >
                    <RotateCw className={`w-3.5 h-3.5 ${submitting ? 'animate-spin' : ''}`} />
                    Retry System Processing
                  </button>
                )}
              </div>
            </div>
          )}

          {activeTab === 'details' && (
            <div className="space-y-4 text-xs">
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-2">
                <h3 className="font-bold text-slate-900 text-sm">System Decision Rationale</h3>
                <p className="text-slate-600 bg-white p-3 border border-slate-200 rounded-lg">
                  {doc.decisionDetails || 'No automated decision rationale recorded.'}
                </p>
              </div>

              {/* Structured findings (D1) — replaces the raw judgment JSON dump */}
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3">
                <h4 className="font-bold text-slate-800">System Findings</h4>
                {!engine.scanned && !verdictChip && !judgment.status ? (
                  <p className="text-slate-500">This document has not been scanned yet. Use the Review action on the documents list to run the system review.</p>
                ) : (
                  <div className="space-y-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <span
                        className="inline-flex items-center px-1.5 py-px rounded-md text-[10px] font-semibold bg-slate-100 text-slate-500 ring-1 ring-slate-200"
                        title={engine.title}
                      >
                        {engine.label}
                      </span>
                      {verdictChip && (
                        <span className={`inline-flex items-center px-1.5 py-px rounded-md text-[10px] font-semibold ${VERDICT_TONE_CLASSES[verdictChip.tone]}`}>
                          {verdictChip.label}
                        </span>
                      )}
                      {mismatch && (
                        <span className="inline-flex items-center px-1.5 py-px rounded-md text-[10px] font-bold bg-red-50 text-red-700 ring-1 ring-red-200">
                          Identity mismatch — review individually
                        </span>
                      )}
                    </div>

                    <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
                      {identityStatus && (
                        <div>
                          <dt className="text-slate-500">Identity cross-check</dt>
                          <dd className={`font-semibold ${identityStatus === 'mismatch' ? 'text-red-600' : 'text-slate-800'}`}>{identityStatus}</dd>
                        </div>
                      )}
                      {judgment.status && (
                        <div>
                          <dt className="text-slate-500">Review run</dt>
                          <dd className="font-semibold text-slate-800">{judgment.status}</dd>
                        </div>
                      )}
                      {doc.verdict?.checkedAt && (
                        <div>
                          <dt className="text-slate-500">Checked at</dt>
                          <dd className="font-semibold text-slate-800">{new Date(doc.verdict.checkedAt).toLocaleString()}</dd>
                        </div>
                      )}
                      {judgment.modelUsed && (
                        <div>
                          <dt className="text-slate-500">Review model</dt>
                          <dd className="font-semibold text-slate-800">{judgment.modelUsed}</dd>
                        </div>
                      )}
                      {doc.extraction?.status && (
                        <div>
                          <dt className="text-slate-500">Scan status</dt>
                          <dd className="font-semibold text-slate-800">{doc.extraction.status}</dd>
                        </div>
                      )}
                      {typeof doc.extraction?.overallConfidence === 'number' && (
                        <div>
                          <dt className="text-slate-500">Scan confidence</dt>
                          <dd className="font-semibold text-slate-800">{Math.round(doc.extraction.overallConfidence * 100)}%</dd>
                        </div>
                      )}
                      {doc.extraction?.processedAt && (
                        <div>
                          <dt className="text-slate-500">Scanned at</dt>
                          <dd className="font-semibold text-slate-800">{new Date(doc.extraction.processedAt).toLocaleString()}</dd>
                        </div>
                      )}
                    </dl>

                    {doc.extraction?.engineNote && (
                      <p className="text-slate-500 bg-white border border-slate-200 rounded-lg p-2.5">{doc.extraction.engineNote}</p>
                    )}

                    {judgment.status === 'failed' && judgment.errorMessage && (
                      <p className="text-rose-700 bg-rose-50 border border-rose-200 rounded-lg p-2.5 font-medium">
                        The system review failed: {judgment.errorMessage}
                      </p>
                    )}

                    {checks.length > 0 && (
                      <div>
                        <h5 className="font-bold text-slate-800 mb-1.5">Checks</h5>
                        <ul className="space-y-1">
                          {checks.map((check: any, idx: number) => (
                            <li key={idx} className="flex items-start gap-2 bg-white border border-slate-200 rounded-lg px-2.5 py-1.5">
                              {check.passed ? (
                                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0 mt-px" />
                              ) : (
                                <XCircle className="w-3.5 h-3.5 text-red-500 shrink-0 mt-px" />
                              )}
                              <span>
                                <span className="font-semibold text-slate-800">{check.name || check.label || 'Check'}</span>
                                {check.details ? <span className="text-slate-500"> — {check.details}</span> : null}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {extractedFields && Object.keys(extractedFields).length > 0 && (
                      <div>
                        <h5 className="font-bold text-slate-800 mb-1.5">Extracted fields</h5>
                        <dl className="bg-white border border-slate-200 rounded-lg divide-y divide-slate-100">
                          {Object.entries(extractedFields).map(([field, value]) => (
                            <div key={field} className="flex items-center justify-between gap-4 px-2.5 py-1.5">
                              <dt className="text-slate-500">{field}</dt>
                              <dd className="font-semibold text-slate-800 text-right">{formatExtractedFieldValue(value)}</dd>
                            </div>
                          ))}
                        </dl>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'history' && (
            <div className="space-y-4 text-xs">
              <h3 className="font-bold text-slate-900 text-sm">Document Review History & Audit Trail</h3>
              <div className="divide-y divide-slate-200 border border-slate-200 rounded-xl overflow-hidden bg-white">
                <div className="p-3 bg-slate-50 font-semibold text-slate-700 flex items-center justify-between">
                  <span>Current Version: {doc.version}</span>
                  <span>Uploaded: {new Date(doc.uploadedAt).toLocaleString()}</span>
                </div>
                {Array.isArray(doc.reviewLogs) && doc.reviewLogs.length > 0 ? (
                  doc.reviewLogs.map((log: any, idx: number) => (
                    <div key={idx} className="p-3 hover:bg-slate-50 flex items-start justify-between gap-4">
                      <div>
                        <div className="font-bold text-slate-900">{log.action || 'Action logged'}</div>
                        <div className="text-slate-500">{log.notes || 'No notes attached.'}</div>
                      </div>
                      <div className="text-right text-slate-400 shrink-0">
                        <div>{log.actor || 'System'}</div>
                        <div>{log.at ? new Date(log.at).toLocaleString() : ''}</div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="p-4 text-slate-500 text-center">No historical audit logs recorded for this document version.</div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'override' && (
            <div className="space-y-4 text-xs max-w-xl">
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-2 text-amber-900">
                <h3 className="font-bold flex items-center gap-1.5 text-sm">
                  <Shield className="w-4 h-4 text-amber-600" />
                  Explicit Administrative Override
                </h3>
                <p>
                  Authorized administrators may override completed system decisions. Overrides require an explicit reason (minimum 20 characters) and will create an immutable audit log entry.
                </p>
              </div>

              <div className="space-y-3">
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Target Status:</label>
                  <select
                    value={overrideTargetStatus}
                    onChange={e => setOverrideTargetStatus(e.target.value)}
                    className="w-full p-2 border border-slate-300 rounded-lg text-xs bg-white focus:ring-2 focus:ring-brand/20"
                  >
                    <option value="Approved">Approved</option>
                    <option value="Rejected">Rejected</option>
                    <option value="PendingAdminReview">Pending Admin Review</option>
                  </select>
                </div>

                <div>
                  <label className="block font-bold text-slate-700 mb-1">Explicit Reason (Min 20 characters):</label>
                  <textarea
                    rows={3}
                    placeholder="Provide a detailed justification for overriding the system decision..."
                    value={overrideReason}
                    onChange={e => setOverrideReason(e.target.value)}
                    className="w-full p-2.5 border border-slate-300 rounded-lg text-xs focus:ring-2 focus:ring-brand/20"
                  />
                  <div className="text-[11px] text-slate-500 mt-1">
                    Character count: {overrideReason.trim().length} / 20 required
                  </div>
                </div>

                <button
                  onClick={handleAdminOverride}
                  disabled={submitting || overrideReason.trim().length < 20}
                  className="w-full py-2.5 bg-brand text-white font-bold rounded-xl hover:bg-brand/90 transition-colors disabled:opacity-50 cursor-pointer shadow-sm"
                >
                  Apply Administrative Override
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
